import { LIVE_INPUT_MIME } from "./protocol";
import type {
  DomActionCallPayload,
  DomActionResultPayload,
  IncomingMessage,
  LiveRealtimePayload,
  PageContextCallPayload,
  PageContextResultPayload,
} from "./protocol";

export interface LiveClientHandlers {
  onAudio: (base64: string, mimeType: string) => void;
  onTranscription: (role: "user" | "model", text: string) => void;
  onTurnComplete: () => void;
  onInterrupted: () => void;
  onSessionTimer: (remainingSeconds: number, totalSeconds: number) => void;
  onSessionEnded: () => void;
  onError: (message: string) => void;
  onClose: (code: number) => void;
  onDomActionCall: (payload: DomActionCallPayload) => void;
  onPageContextCall: (payload: PageContextCallPayload) => void;
}

function randomId(): string {
  if ("randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function toWsUrl(apiUrl: string): string {
  return `${apiUrl.replace(/^http/, "ws")}/ai-service/api/v1/copilot/wss`;
}

export class LiveClient {
  private ws: WebSocket | null = null;
  private readonly streamSid = randomId();
  private readonly conversationId: string;
  private readonly handlers: LiveClientHandlers;

  constructor(conversationId: string, handlers: LiveClientHandlers) {
    this.conversationId = conversationId;
    this.handlers = handlers;
  }

  connect(apiUrl: string, agentId: string, token: string): void {
    const ws = new WebSocket(toWsUrl(apiUrl));
    this.ws = ws;

    ws.onopen = () => {
      ws.send(
        JSON.stringify({
          event: "connect",
          stream_sid: this.streamSid,
          conversation_id: this.conversationId,
          payload: {
            agent_id: agentId,
            debug: false,
            token,
          },
        }),
      );
    };

    ws.onmessage = (evt) => this.handleMessage(evt.data);
    ws.onclose = (evt) => this.handlers.onClose(evt.code);
    ws.onerror = () => this.handlers.onError("Connection error");
  }

  private handleMessage(raw: string): void {
    let message: IncomingMessage;
    try {
      message = JSON.parse(raw);
    } catch {
      return;
    }

    switch (message.event) {
      case "live_realtime":
        if (message.payload.channel === "audio" && message.payload.data_b64) {
          this.handlers.onAudio(
            message.payload.data_b64,
            message.payload.mime_type || "audio/pcm;rate=24000",
          );
        }
        break;
      case "live_transcription":
        this.handlers.onTranscription(
          message.live_transcription.role,
          message.live_transcription.text,
        );
        break;
      case "live_turn_complete":
        this.handlers.onTurnComplete();
        break;
      case "live_interrupted":
        this.handlers.onInterrupted();
        break;
      case "live_session_timer":
        this.handlers.onSessionTimer(
          message.live_session_timer.remaining_seconds,
          message.live_session_timer.total_seconds,
        );
        break;
      case "live_session_ended":
        this.handlers.onSessionEnded();
        break;
      case "live_ping":
        // Answered immediately, no app logic involved -- the server uses
        // the round trip itself to detect a connection that died silently
        // (no close frame ever reached it), which it can't otherwise tell
        // apart from a healthy call that's just quiet for a while.
        this.sendPong();
        break;
      case "error":
        this.handlers.onError(message.message);
        break;
      case "dom_action_call":
        this.handlers.onDomActionCall(message.dom_action_call);
        break;
      case "page_context_call":
        this.handlers.onPageContextCall(message.page_context_call);
        break;
    }
  }

  sendAudioChunk(base64: string): void {
    this.sendRealtime({ channel: "audio", mime_type: LIVE_INPUT_MIME, data_b64: base64 });
  }

  endAudioStream(): void {
    this.sendRealtime({ channel: "audio", audio_stream_end: true });
  }

  private sendPong(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(
      JSON.stringify({
        event: "live_pong",
        stream_sid: this.streamSid,
        conversation_id: this.conversationId,
        payload: { name: "pong" },
      }),
    );
  }

  sendDomActionResult(payload: DomActionResultPayload): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(
      JSON.stringify({
        event: "dom_action_result",
        stream_sid: this.streamSid,
        conversation_id: this.conversationId,
        payload,
      }),
    );
  }

  sendPageContextResult(payload: PageContextResultPayload): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(
      JSON.stringify({
        event: "page_context_result",
        stream_sid: this.streamSid,
        conversation_id: this.conversationId,
        payload,
      }),
    );
  }

  private sendRealtime(payload: LiveRealtimePayload): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(
      JSON.stringify({
        event: "live_realtime",
        stream_sid: this.streamSid,
        conversation_id: this.conversationId,
        payload,
      }),
    );
  }

  close(): void {
    this.ws?.close();
  }
}
